import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchHealth } from './health';
import { BUSINESS_TIME_ZONE, businessMonth } from './ledger/calendar';
import { contextEquals, withChange, type LedgerContext } from './ledger/context';
import { LedgerWorkspace } from './ledger/LedgerWorkspace';
import { RecurrenceWorkspace } from './ledger/RecurrenceWorkspace';
import { ReportsWorkspace } from './ledger/ReportsWorkspace';
import { NOT_IMPLEMENTED, shortRevision } from './notImplemented';
import { useAuthenticatedSession } from './session/SessionBoundary';
import { Menu } from './shell/Menu';
import {
  readLocation,
  replayedLocation,
  urlForLocation,
  type Location,
  type Screen,
} from './shell/screens';

/**
 * The signed-in shell (P2-T2, extended by P4-T2, P5-T1, P5-T2, P6-T2, P7, P8 and the C4a
 * navigation fix).
 *
 * This component is mounted **only** behind the session boundary, so an unauthenticated visitor
 * never reaches it. It is the app's frame: a one-row menu bar across the top, one screen per
 * purpose, and the owner's own session at the screen's foot.
 *
 * **The address is the shell's.** `screens.ts` reads a path into a screen and this component writes
 * the address back through one `go` — so the ledger's own controls ask for a context change
 * (`navigate`) and the menu asks for a screen (`show`), and neither of them touches `history`. Two
 * components writing the address is how a query string and a path come to disagree.
 *
 * The screens are the purposes the app already had, and nothing else: `/` is the ledger (with the
 * query string P4-T2 won, untouched), `/accounts` is the directory's management half with the
 * categories, `/recurring` is the recurrence workspace, `/reports` is the reporting workspace. A
 * path that names none of them is replaced by its default rather than shown as an error. P9's
 * *Imports and exports* adds `/import` and a fifth menu item when it ships.
 *
 * What is left of the shell itself: the menu, the session block, and — on the ledger screen, where
 * they have always been — the application's status and the `NOT_IMPLEMENTED` note, whose only
 * source is `./notImplemented.ts` and whose guard reads it on every run so an entry cannot outlive
 * the feature it denies.
 */
export function App() {
  const session = useAuthenticatedSession();
  const [busy, setBusy] = useState<'sign-out' | 'check' | undefined>(undefined);
  const fallbackMonth = businessMonth(BUSINESS_TIME_ZONE, new Date());

  const [location, setLocation] = useState<Location>(() =>
    readLocation(window.location.pathname, window.location.search, fallbackMonth),
  );

  /**
   * Write an address and render it.
   *
   * The shell's only writer: every navigation below ends here, so the address and the screen on
   * screen are set together and cannot drift.
   */
  const go = useCallback((next: Location, options: { readonly replace?: boolean } = {}) => {
    const url = urlForLocation(next);
    if (options.replace === true) window.history.replaceState(null, '', url);
    else window.history.pushState(null, '', url);
    setLocation(next);
  }, []);

  // Back and forward move the screen and the ledger's context, because both live in the address.
  useEffect(() => {
    const onPopState = (): void => {
      const address = readLocation(window.location.pathname, window.location.search, fallbackMonth);
      setLocation((held) => replayedLocation(held, address));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [fallbackMonth]);

  // Canonicalise the address once, so the first view is linkable and comparable — and so a path
  // naming no screen is replaced by its default rather than rendered as an error.
  useEffect(() => {
    const canonical = urlForLocation(location);
    if (`${window.location.pathname}${window.location.search}` !== canonical) {
      window.history.replaceState(null, '', canonical);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** The menu's navigation: another screen, keeping the ledger's context. */
  const show = useCallback(
    (screen: Screen) => {
      if (screen === location.screen) return;
      go({ screen, context: location.context });
    },
    [go, location],
  );

  /**
   * The ledger's navigation: a changed context, on the ledger's own address.
   *
   * On the accounts screen this is what an account row does — it opens the ledger on that account,
   * which is the reason the condition below is about the *context* having changed and the screen
   * not: clicking the account you were already on still moves you to where it is shown.
   */
  const navigate = useCallback(
    (patch: Partial<LedgerContext>, options: { readonly replace?: boolean } = {}) => {
      const next = withChange(location.context, patch);
      if (location.screen === 'ledger' && contextEquals(next, location.context)) return;
      go({ screen: 'ledger', context: next }, options);
    },
    [go, location],
  );

  const health = useQuery({
    queryKey: ['health'],
    queryFn: ({ signal }) => fetchHealth(signal),
  });

  return (
    <main className="shell">
      {/*
        * The menu bar: one row across the top, at every width, rendered here and nowhere else.
        * Sign out lives in it, so ending the session is one press from every screen. The private
        * region it ends is the footer below — the bar is not session content.
        */}
      <Menu
        screen={location.screen}
        onSelect={show}
        signingOut={busy === 'sign-out'}
        onSignOut={() => {
          setBusy('sign-out');
          void session.signOut().finally(() => setBusy(undefined));
        }}
      />

      {/*
        * One screen at a time. The ledger screen carries the two panels that belong to the shell
        * rather than to the month: the application's status and the note about what is not
        * implemented in this revision.
        */}
      <div className="screen">
        {location.screen === 'ledger' ? (
          <>
            <LedgerWorkspace screen="ledger" context={location.context} navigate={navigate} />

            <section aria-labelledby="status-heading">
              <h2 id="status-heading">Application status</h2>
              {health.isPending ? <p>Contacting the API…</p> : null}
              {health.isError ? (
                <p className="status-bad">The API did not answer. Try again in a moment.</p>
              ) : null}
              {health.data ? (
                <dl className="status-grid">
                  <dt>Revision</dt>
                  <dd>{health.data.revision}</dd>
                  <dt>Version</dt>
                  <dd>{health.data.version}</dd>
                  <dt>Database</dt>
                  <dd>{health.data.database.reachable ? 'reachable' : 'not reachable'}</dd>
                </dl>
              ) : null}
            </section>

            <section aria-labelledby="pending-heading">
              <h2 id="pending-heading">Not implemented in this revision</h2>
              <ul>
                {NOT_IMPLEMENTED.map((item) => (
                  <li key={item.phase}>
                    {item.label}{' '}
                    <span className="muted">— absent as of revision {shortRevision(item.verifiedAt)}</span>
                  </li>
                ))}
              </ul>
              <p className="muted">
                Each entry above is re-checked against the code, the delivery plan and the acceptance
                register on every run, so it cannot outlive the feature it denies.
              </p>
            </section>
          </>
        ) : null}

        {location.screen === 'accounts' ? (
          <LedgerWorkspace screen="accounts" context={location.context} navigate={navigate} />
        ) : null}

        {location.screen === 'recurring' ? <RecurrenceWorkspace /> : null}

        {location.screen === 'reports' ? <ReportsWorkspace /> : null}
      </div>

      {/*
        * The session block, at the screen's foot — *"Your session goes to the bottom."* It is in the
        * shell rather than on one screen, because the owner id and "Check session" are the owner's
        * own region and are expected on every screen: 41 existing waits on `owner-id` across ten
        * browser specs read it wherever they are, and the ledger is only one of the screens.
        *
        * It is private content: it lives behind the boundary, it is dropped the moment the session
        * ends, and it is never persisted.
        */}
      <footer className="session-foot" data-private="session" aria-labelledby="session-heading">
        <h2 id="session-heading">Your session</h2>
        <dl className="status-grid">
          <dt>Owner</dt>
          <dd>
            <code data-testid="owner-id">{session.ownerId}</code>
          </dd>
        </dl>
        <p className="actions">
          <button
            type="button"
            data-testid="check-session"
            aria-disabled={busy !== undefined}
            onClick={() => {
              setBusy('check');
              void session.revalidate().finally(() => setBusy(undefined));
            }}
          >
            {busy === 'check' ? 'Checking…' : 'Check session'}
          </button>
        </p>
      </footer>
    </main>
  );
}
